import type {
  DependencyMapNode,
  DependencyMapReference,
  ReferenceAddress,
} from '../types';

interface AggregateCollapsedReferencesReducerState {
  result: Map<string, DependencyMapReference[]>;
  nodeAddressMap: Map<string, ReferenceAddress[]>;
}
const aggregateCollapsedReferencesReducer = (
  state: AggregateCollapsedReferencesReducerState,
  reference: DependencyMapReference
) => {
  const distinctKey = `${reference.referenceTypeId}-${reference.node.modelId}-${reference.label}-${reference.otherLabels?.flat().join(',') ?? ''}`;
  if (!state.result.has(distinctKey)) {
    state.result.set(distinctKey, []);
  }
  const referenceGroup = state.result.get(distinctKey)!;
  if (referenceGroup.length) {
    const referenceAddress = state.nodeAddressMap.get(reference.node.id) ?? [];
    state.nodeAddressMap
      .get(referenceGroup[0].node.id)
      ?.push(referenceAddress[0]);
  }
  referenceGroup.push(reference);
  return state;
};

interface NodeCollapsingReducerState {
  result: DependencyMapNode[];
  depth: number;
  collapsedIncomingReferences: DependencyMapReference[];
  collapsedOutgoingReferences: DependencyMapReference[];
  collapsedCombinedReferences: DependencyMapReference[];
  nodeAddressMap: Map<string, ReferenceAddress[]>;
}
const nodeCollapsingReducer = (
  {
    result,
    depth,
    collapsedIncomingReferences,
    collapsedOutgoingReferences,
    collapsedCombinedReferences,
    nodeAddressMap,
  }: NodeCollapsingReducerState,
  node: DependencyMapNode
): NodeCollapsingReducerState => {
  const {
    result: newChildren,
    collapsedIncomingReferences: collapsedChildrenIncomingReferences,
    collapsedOutgoingReferences: collapsedChildrenOutgoingReferences,
    collapsedCombinedReferences: collapsedChildrenCombinedReferences,
  } = node.children?.reduce(nodeCollapsingReducer, {
    result: [],
    depth: depth - 1,
    collapsedIncomingReferences: [],
    collapsedOutgoingReferences: [],
    collapsedCombinedReferences: [],
    nodeAddressMap,
  }) ?? {
    result: null,
    collapsedIncomingReferences: [],
    collapsedOutgoingReferences: [],
    collapsedCombinedReferences: [],
  };
  const nodeIncomingReferences = node.incomingReferences ?? [];
  const nodeOutgoingReferences = node.outgoingReferences ?? [];
  const nodeCombinedReferences = node.combinedReferences ?? [];

  if (depth > 0) {
    const hadChildren = node.children?.length;
    node.children = newChildren;
    const allIncomingReferences = [
      ...nodeIncomingReferences,
      ...collapsedChildrenIncomingReferences,
    ];
    const allOutgoingReferences = [
      ...nodeOutgoingReferences,
      ...collapsedChildrenOutgoingReferences,
    ];
    const allCombinedReferences = [
      ...nodeCombinedReferences,
      ...collapsedChildrenCombinedReferences,
    ];
    const [
      aggregatedIncomingReferences,
      aggregatedOutgoingReferences,
      aggregatedCombinedReferences,
    ] = [
      allIncomingReferences,
      allOutgoingReferences,
      allCombinedReferences,
    ].map(
      references =>
        references.reduce(aggregateCollapsedReferencesReducer, {
          result: new Map(),
          nodeAddressMap,
        }).result
    );

    const [
      newIncomingReferences,
      newOutgoingReferences,
      newCombinedReferences,
    ] = [
      aggregatedIncomingReferences,
      aggregatedOutgoingReferences,
      aggregatedCombinedReferences,
    ].map(aggregatedReferenceMap =>
      [...aggregatedReferenceMap.values()].map<DependencyMapReference>(
        ([firstAggregatedReference, ...restOfAggregatedReferences]) => ({
          ...firstAggregatedReference,
          referenceIds: [
            ...firstAggregatedReference.referenceIds,
            ...restOfAggregatedReferences.flatMap(
              ({ referenceIds }: { referenceIds: string[] }) => referenceIds
            ),
          ],
        })
      )
    );
    node.incomingReferences = newIncomingReferences;
    node.outgoingReferences = newOutgoingReferences;
    node.combinedReferences = newCombinedReferences;
    if (hadChildren && depth === 1) {
      node.containsCollapsedNodes = true;
    }
    result.push(node);
  } else {
    collapsedIncomingReferences.push(...collapsedChildrenIncomingReferences);
    collapsedOutgoingReferences.push(...collapsedChildrenOutgoingReferences);
    collapsedCombinedReferences.push(...collapsedChildrenCombinedReferences);
    collapsedIncomingReferences.push(...nodeIncomingReferences);
    collapsedOutgoingReferences.push(...nodeOutgoingReferences);
    collapsedCombinedReferences.push(...nodeCombinedReferences);
    [
      ...collapsedIncomingReferences,
      ...collapsedOutgoingReferences,
      ...collapsedCombinedReferences,
    ].forEach(reference => (reference.isCollapsedReference = true));
    const nodeAddresses = nodeAddressMap.get(node.id);
    nodeAddresses?.forEach(nodeAddress => {
      if (nodeAddress?.length) {
        nodeAddress[nodeAddress.length - 1].nodeInfo.isHidden = true; // this nodeInfo thing is part of many different addresses in the map. it must be mutated here, not replaced!
      }
    });
  }

  return {
    result,
    depth,
    collapsedIncomingReferences,
    collapsedOutgoingReferences,
    collapsedCombinedReferences,
    nodeAddressMap,
  };
};
const collapseNodes = (
  nodes: DependencyMapNode[],
  maxDepth: number,
  nodeAddressMap: Map<string, ReferenceAddress[]>
) =>
  nodes.reduce(nodeCollapsingReducer, {
    result: [],
    depth: maxDepth,
    collapsedIncomingReferences: [],
    collapsedOutgoingReferences: [],
    collapsedCombinedReferences: [],
    nodeAddressMap,
  });
export { collapseNodes };
